import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SearchBar from "@/components/feature/SearchBar";
import Select from "@/components/base/Select";
import Button from "@/components/base/Button";
import EmptyState from "@/components/feature/EmptyState";
import ConfirmDialog from "@/components/feature/ConfirmDialog";
import CategoryBadge from "@/components/feature/CategoryBadge";
import { useToast } from "@/store/ToastContext";
import { formatCurrency } from "@/utils/currency";
import { formatShortDate } from "@/utils/date";
import { formatFileSize } from "@/utils/format";
import type { Receipt, Expense, Category, AppSettings } from "@/models/types";
import { expenseApi, categoryApi, settingsApi } from "@/services/api";

interface ReceiptEntry {
  receipt: Receipt;
  expenseId: string;
  description: string;
  categoryId: string;
  amount: number;
  date: string;
}

export default function Receipts() {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [toDelete, setToDelete] = useState<ReceiptEntry | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [expensesData, categoriesData, settingsData] = await Promise.all([
          expenseApi.list(),
          categoryApi.list(),
          settingsApi.get(),
        ]);

        // Mapear si es necesario (aunque expenseApi.list ya devuelve camelCase)
        const mappedExpenses = expensesData.map((e: any) => ({
          ...e,
          categoryId: e.category_id ?? e.categoryId,
          paymentMethod: e.payment_method ?? e.paymentMethod,
          createdAt: e.created_at ?? e.createdAt,
          receipt: e.receipt ?? null,
        }));

        setExpenses(mappedExpenses);
        setCategories(categoriesData);
        setSettings(settingsData);
      } catch (error) {
        console.error("Error cargando recibos:", error);
        showToast("Error al cargar los recibos.", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const currency = settings?.currency ?? "USD";

  const allReceipts: ReceiptEntry[] = useMemo(
    () =>
      expenses
        .filter((e) => e.receipt)
        .map((e) => ({
          receipt: e.receipt!,
          expenseId: e.id,
          description: e.description,
          categoryId: e.categoryId,
          amount: e.amount,
          date: e.date,
        })),
    [expenses],
  );

  const filtered = useMemo(() => {
    return allReceipts.filter((r) => {
      if (search && !r.receipt.fileName.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (category && r.categoryId !== category) return false;
      if (type && r.receipt.fileType !== type) return false;
      if (dateFrom && r.date < dateFrom) return false;
      if (dateTo && r.date > dateTo) return false;
      return true;
    });
  }, [allReceipts, search, category, type, dateFrom, dateTo]);

  const handleDelete = async () => {
    if (!toDelete) return;
    const expense = expenses.find((e) => e.id === toDelete.expenseId);
    if (!expense) {
      setToDelete(null);
      return;
    }

    try {
      // Actualizar el gasto en el backend quitando el recibo
      await expenseApi.update(expense.id, { receipt: null } as any);
      // Actualizar estado local
      setExpenses((prev) =>
        prev.map((e) => (e.id === expense.id ? { ...e, receipt: null } : e)),
      );
      showToast("Recibo eliminado correctamente.");
    } catch (error) {
      console.error("Error eliminando recibo:", error);
      showToast("Error al eliminar el recibo.", "error");
    } finally {
      setToDelete(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-4 mb-5">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nombre de archivo..."
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={[
              { value: "", label: "Todas las categorías" },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={[
              { value: "", label: "Todos los tipos" },
              { value: "image", label: "Imagen" },
              { value: "pdf", label: "PDF" },
            ]}
          />
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <span className="text-foreground-500">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>
      </div>

      <p className="text-sm text-foreground-500 mb-3">
        {filtered.length} recibo{filtered.length !== 1 ? "s" : ""}
      </p>

      {filtered.length === 0 ? (
        <div className="bg-background-50 border border-background-200 rounded-lg">
          <EmptyState
            icon="ri-receipt-line"
            title="Sin recibos"
            description="Cuando subas una foto o PDF de un gasto, aparecerá aquí."
            action={
              <Link
                to="/gastos/nuevo"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-line" /> Registrar gasto con recibo
              </Link>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <div
              key={r.receipt.id || r.expenseId}
              className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
            >
              <div className="aspect-[4/3] bg-background-100 flex items-center justify-center overflow-hidden">
                {r.receipt.fileType === "image" ? (
                  <img
                    src={r.receipt.url}
                    alt={r.receipt.fileName}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <span className="w-16 h-16 rounded-lg bg-red-100 text-red-500 flex items-center justify-center">
                    <i className="ri-file-pdf-2-line text-4xl" />
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {r.receipt.fileName}
                  </p>
                  <span className="text-xs text-foreground-500 uppercase shrink-0">
                    {r.receipt.fileType}
                  </span>
                </div>
                <Link
                  to={`/gastos/${r.expenseId}`}
                  className="text-sm text-primary-600 hover:text-primary-700 truncate block mt-1 cursor-pointer"
                >
                  {r.description}
                </Link>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CategoryBadge categoryId={r.categoryId} showName={false} />
                    <span className="text-xs text-foreground-500">
                      {formatShortDate(r.date)}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                    {formatCurrency(r.amount, currency)}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
                  <span className="text-xs text-foreground-500">
                    {formatFileSize(r.receipt.fileSize)}
                  </span>
                  <div className="flex items-center gap-1">
                    <a
                      href={r.receipt.url}
                      download={r.receipt.fileName}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-secondary-200 text-foreground-600 cursor-pointer"
                      title="Descargar"
                    >
                      <i className="ri-download-line" />
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="ri-delete-bin-line"
                      onClick={() => setToDelete(r)}
                      className="!px-2 text-red-500 hover:bg-red-50"
                      title="Eliminar"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete}
        title="Eliminar recibo"
        message={`¿Eliminar el recibo "${toDelete?.receipt.fileName}"? El gasto se mantendrá, solo se quitará el archivo adjunto.`}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </AppLayout>
  );
}